/** A number with the extension typed after it ("ext. 123", "ext 123" or "x123"), split from it. */
export function splitExtension(
  typed: string
): Readonly<{ number: string; extension: string | null }> {
  const match = /^(.*?)[\s,;]*(?:ext\.?|x)\s*(\d+)\s*$/i.exec(typed);
  return match
    ? { number: match[1], extension: match[2] }
    : { number: typed, extension: null };
}

/**
 * A phone number as a screen shows it, for the numbers this module can be sure of: a North American number, ten digits
 * or eleven with the 1 in front, with or without a +, written "+1 (316) 258-4026" with any extension as "ext. 12". A
 * number in another country, or one whose area code no North American number has, is returned as written: telling the
 * other countries' numbers apart takes the numbering plans, and a wrong grouping or code is worse than none.
 *
 * This module has no dependencies so the dashboard can use it. The console, which can carry the numbering plans, writes
 * numbers from other countries before they reach the library (`@cowbell/broker/phone`).
 */
export function formatNanpPhone(phone: string | null | undefined): string {
  const written = phone?.trim() ?? '';
  if (!written) {
    return '';
  }
  const { number, extension } = splitExtension(written);
  const digits = number.replace(/\D/g, '');
  const national = digits.length === 11 ? digits.slice(1) : digits;
  const isNanp =
    national.length === 10 &&
    (digits.length === 10 || digits.startsWith('1')) &&
    !/^[01]/.test(national);
  if (!isNanp || (number.trimStart().startsWith('+') && digits.length !== 11)) {
    return written;
  }
  const formatted = `+1 (${national.slice(0, 3)}) ${national.slice(
    3,
    6
  )}-${national.slice(6)}`;
  return extension ? `${formatted} ext. ${extension}` : formatted;
}
